import * as React from "react";
import { useState, useMemo } from "react";
import { Formik, FormikProps } from "formik";
import { SingleFileUploader } from "@graffer-inc/console-design-kit";
import { BaseModal } from "../../../atoms/base-modal";
import { GroupShareSelectField } from "../../../molecules/group-share-select-field";
import classNames from "classnames";
import { ProcedureImportStatusType } from "../../../../application/domain/procedures/procedure-import-status";
import { emptyFunction } from "../../../../utils/function-utils";

export const ProcedureImportFromZipFileModal: React.FC<{
  isActive: boolean;
  isLoading: boolean;
  groupId?: string;
  close: () => void;
  onSave: (
    zipFile: File,
    shallClearCustomPath: boolean,
    groupId?: string,
  ) => void;
  checkImportFile: (
    jsonData: File,
  ) => Promise<ProcedureImportStatusType | void>;
}> = ({ isActive, isLoading, groupId, close, onSave, checkImportFile }) => {
  const [isCheckingImportFile, setIsCheckingImportFile] =
    useState<boolean>(false);
  const [zipFile, setZipFile] = useState<File>();
  const [selectedGroupId, setGroupId] = useState<string>(groupId || "");
  const [invalidZip, setInvalidZip] = useState<string>();
  const [invalidFileVersion, setInvalidFileVersion] = useState<string>();
  const [invalidJson, setInvalidJson] = useState<string>();
  const [duplicatedCustomPathFileName, setDuplicatedCustomPathFileName] =
    useState<string>();
  const [shallClearCustomPath, setShallClearCustomPath] = useState(false);

  const invalidFile = useMemo((): boolean => {
    return !!invalidZip || !!invalidFileVersion || !!invalidJson;
  }, [invalidZip, invalidFileVersion, invalidJson]);

  const canImportWithCustomPathSetting = useMemo((): boolean => {
    return (
      !invalidFile && (!duplicatedCustomPathFileName || shallClearCustomPath)
    );
  }, [invalidFile, duplicatedCustomPathFileName, shallClearCustomPath]);

  const validateInputFile = useMemo((): string => {
    let msg = "";
    if (canImportWithCustomPathSetting) return msg;
    if (invalidFile) {
      msg += "ファイル形式が正しくありません。";
      if (invalidZip)
        msg += "エクスポートした手続きファイルを指定してください。";
      if (invalidJson || invalidFileVersion)
        msg += "手続きを再度エクスポートし、ファイルを指定してください。";
      return msg;
    }
    if (duplicatedCustomPathFileName)
      msg += "申請開始ページURLがすでに登録されているファイルがあります。";
    return msg;
  }, [
    canImportWithCustomPathSetting,
    invalidFile,
    invalidFileVersion,
    duplicatedCustomPathFileName,
    shallClearCustomPath,
  ]);

  const checkFile = async (data: File) => {
    setIsCheckingImportFile(true);
    const result = await checkImportFile(data);
    if (result !== undefined) {
      if (result.hasInvalidZip) setInvalidZip(data.name);
      if (result.hasInvalidFileVersion) setInvalidFileVersion(data.name);
      if (result.hasDuplicateCustomPath)
        setDuplicatedCustomPathFileName(data.name);
      if (result.hasInvalidJson) setInvalidJson(data.name);
    }
    setIsCheckingImportFile(false);
  };

  const initForm = () => {
    setZipFile(undefined);
    setInvalidZip("");
    setInvalidFileVersion("");
    setInvalidJson("");
    setShallClearCustomPath(false);
    setDuplicatedCustomPathFileName("");
  };

  return (
    <BaseModal
      isActive={isActive}
      title="手続きインポート"
      onClose={close}
      hasOverflow={true}
    >
      <Formik
        validateOnChange={true}
        initialValues={{}}
        onSubmit={emptyFunction}
      >
        {({ handleSubmit }: FormikProps<object>) => (
          <form onSubmit={handleSubmit}>
            <section className="modal-card-body">
              <div className="field">
                <div
                  className={classNames("file has-name", {
                    "is-danger": !canImportWithCustomPathSetting,
                  })}
                >
                  <label className="file-label">
                    <div className="field">
                      <SingleFileUploader
                        acceptFileType={["application/zip"]}
                        deleteFile={() => {
                          initForm();
                          setZipFile(undefined);
                        }}
                        description="エクスポートした手続き情報ファイルから、手続きを新規作成します。"
                        errorMessages={validateInputFile}
                        label="手続き情報ファイル"
                        required
                        setFile={(file) => {
                          if (!file) {
                            return;
                          }
                          initForm();
                          if (file instanceof Blob) {
                            const fileReader = new FileReader();
                            fileReader.onload = function () {
                              checkFile(file);
                              setZipFile(file);
                            };
                            fileReader.readAsBinaryString(file);
                          }
                        }}
                        value={zipFile}
                      />
                    </div>
                  </label>
                </div>
              </div>
              {!invalidFile && duplicatedCustomPathFileName && (
                <div className="field">
                  <p className="help is-info">
                    チェックしてインポートすると、このファイルに含まれる申請開始ページURLの設定は削除され、
                    申請開始ページURLが設定されていない手続きとしてインポートされます。
                  </p>
                  <ul>
                    <li>
                      <label className="checkbox">
                        <input
                          type="checkbox"
                          checked={shallClearCustomPath}
                          onChange={() => setShallClearCustomPath((v) => !v)}
                        />
                        &nbsp; {duplicatedCustomPathFileName}
                      </label>
                    </li>
                  </ul>
                </div>
              )}
              <div className="field">
                <GroupShareSelectField
                  selectedGroupId={selectedGroupId || ""}
                  onChange={setGroupId}
                />
              </div>
            </section>
            <section className="modal-card-foot">
              <button
                type="button"
                className="button is-failure is-fullwidth"
                onClick={close}
              >
                キャンセルする
              </button>
              <button
                type="submit"
                className={classNames("button is-info is-fullwidth", {
                  "is-loading": isLoading || isCheckingImportFile,
                })}
                onClick={() => {
                  if (
                    !zipFile ||
                    !selectedGroupId ||
                    !canImportWithCustomPathSetting
                  ) {
                    return;
                  }
                  onSave(zipFile, shallClearCustomPath, selectedGroupId);
                }}
                disabled={
                  !zipFile ||
                  !selectedGroupId ||
                  !canImportWithCustomPathSetting ||
                  isLoading ||
                  isCheckingImportFile
                }
              >
                インポートする
              </button>
            </section>
          </form>
        )}
      </Formik>
    </BaseModal>
  );
};
